/**
 * Where the feedback dialog is opened from.
 *
 * A module-level store rather than a prop, for one reason that is worth the indirection:
 * **the most valuable feedback is sent from a failure**, and failures happen in places that
 * have no path to a dialog — the turn feed, and the error boundary that catches a render
 * crash and has thrown the whole route away. `openFeedback()` is callable from both without
 * either of them knowing a dialog exists.
 *
 * P5's `request_id` is the payload that makes this worth building. Every error the API
 * produces carries one, it is the correlation id in CloudWatch, and a bug report that has it
 * is a log query instead of an investigation. The player never has to see it, copy it, or be
 * asked for it — the affordance that opens this carries it.
 *
 * The route is carried the same way and for the same reason, but it is *mirrored in* rather
 * than read at open time — see `useTrackFeedbackRoute` at the foot of this file.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { create } from 'zustand';
import type { FeedbackCategory } from '@/api/generated';

export interface FeedbackContext {
  category: FeedbackCategory;
  /** The story the player is in, when they are in one. */
  storyId: string | null;
  /** P5's correlation id, when this was opened from an error. */
  requestId: string | null;
  /** The route the report was sent from. Captured by `open()`, not passed by callers. */
  path: string | null;
  /** Seeds the message box — the error's own text, when there is one. */
  prefill: string;
}

interface FeedbackStore {
  /** Null when shut. The context *is* the open state. */
  context: FeedbackContext | null;
  /** The route the player is on, mirrored in by `useTrackFeedbackRoute`. */
  currentPath: string | null;
  setCurrentPath(path: string): void;
  open(context: Partial<FeedbackContext>): void;
  close(): void;
}

export const useFeedbackStore = create<FeedbackStore>()((set, get) => ({
  context: null,
  currentPath: null,
  setCurrentPath: (path) => set({ currentPath: path }),
  open: (context) =>
    set({
      context: {
        // Feedback rather than bug: opening this from the navigation is usually somebody with
        // an opinion, and the two error paths below both pass `category: 'bug'` explicitly.
        category: 'feedback',
        storyId: null,
        requestId: null,
        // Filled in here rather than by callers. Every opener is somewhere the route is
        // already correct — including the error boundary, whose route has crashed but whose
        // URL has not — so asking each of them to pass it would be three chances to forget.
        path: get().currentPath,
        prefill: '',
        ...context,
      },
    }),
  close: () => set({ context: null }),
}));

/** Callable from an event handler, a mutation callback, or a class component's fallback. */
export function openFeedback(context: Partial<FeedbackContext> = {}): void {
  useFeedbackStore.getState().open(context);
}

/**
 * Keeps `currentPath` in step with the router. Called once by `App`.
 *
 * The obvious shortcut is for `open()` to read `window.location.pathname` and skip this
 * entirely. It would work in the browser and quietly lie everywhere else: the app is mounted
 * under `MemoryRouter` in every test, where the router's location and `window.location` are
 * different things, so a report would record `/` no matter which screen produced it — and a
 * test asserting the route would pass for the wrong reason. Mirroring the router's own answer
 * costs one effect and is true under both.
 */
export function useTrackFeedbackRoute(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    useFeedbackStore.getState().setCurrentPath(pathname);
  }, [pathname]);
}
